import { useState, useRef, useEffect } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/app";
import { PushPin, X, Check, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

/** Stable identifier so we can detect already-pinned items. */
function buildKey(kind, data) {
  if (!data) return kind;
  if (kind === "kpi") return `kpi:${data.metric || ""}`;
  if (kind === "slot") return `slot:${data.weekday}-${data.hour}`;
  if (kind === "anomaly") return `anomaly:${data.date || ""}-${data.hour ?? "d"}`;
  if (kind === "day") return `day:${data.date || ""}`;
  if (kind === "weekday") return `weekday:${data.weekday ?? ""}-${data.hour ?? "x"}`;
  if (kind === "chart") return `chart:${data.id || data.metric || ""}`;
  return kind;
}

/**
 * Reusable "pin to highlights" button.
 * Props:
 *  - storeId, kind, label, data, period, className, testId, showLabel
 */
export default function PinButton({ storeId, kind, label, data, period, className = "", testId, showLabel = false }) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  const { data: highlights = [] } = useSWR(storeId ? `/stores/${storeId}/highlights` : null, fetcher);

  const pinKey = buildKey(kind, data);
  const existing = highlights.find((h) => buildKey(h.kind, h.data) === pinKey);
  const isPinned = Boolean(existing);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      if (isPinned) {
        await api.delete(`/highlights/${existing.id}`);
        toast.success("Odznačené");
      } else {
        await api.post("/highlights", {
          store_id: storeId,
          kind,
          label,
          data,
          comment: comment.trim() || null,
          period_from: period?.from || null,
          period_to: period?.to || null,
        });
        toast.success("Označené pre správu");
      }
      mutate(`/stores/${storeId}/highlights`);
      setOpen(false);
      setComment("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba pri ukladaní");
    } finally {
      setSaving(false);
    }
  };

  const pinnedColor = isPinned ? "var(--success)" : "var(--brand)";
  const pinnedBg = isPinned ? "var(--success-bg)" : "transparent";

  return (
    <div className={`relative inline-block ${className}`} ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        title={isPinned ? "Už označené pre správu — kliknite pre detail" : "Označiť pre AI správu a export"}
        data-testid={testId || `pin-${kind}`}
        data-pinned={isPinned}
        style={{ color: pinnedColor, background: pinnedBg }}
        className={`inline-flex items-center gap-1 hover:opacity-80 transition-colors p-1 rounded ${showLabel ? "text-xs px-2 py-1" : ""}`}
      >
        {isPinned ? (
          <CheckCircle size={showLabel ? 13 : 16} weight="fill" />
        ) : (
          <PushPin size={showLabel ? 13 : 16} weight={open ? "fill" : "regular"} />
        )}
        {showLabel && <span>{isPinned ? "Označené" : "Označiť"}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-7 z-30 bg-white border border-[var(--border-default)] rounded-md shadow-lg p-3 w-72" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold">{isPinned ? "Označený bod" : "Označiť pre správu"}</p>
            <button onClick={() => setOpen(false)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
              <X size={12} />
            </button>
          </div>
          <p className="text-xs text-[var(--text-secondary)] mb-2 line-clamp-3">{label}</p>
          {isPinned ? (
            <>
              {existing?.comment && (
                <div className="text-xs bg-[var(--bg-page)] rounded p-2 mb-2 italic text-[var(--text-secondary)]">
                  „{existing.comment}&rdquo;
                </div>
              )}
              <button
                type="button"
                disabled={saving}
                onClick={submit}
                className="w-full mt-1 inline-flex items-center justify-center gap-1 text-xs py-1.5 rounded border border-[var(--critical)] text-[var(--critical)] hover:bg-[var(--critical-bg)]"
                data-testid="pin-unpin-button"
              >
                <X size={12} /> {saving ? "Odznačujem…" : "Odznačiť"}
              </button>
            </>
          ) : (
            <>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder="Komentár (voliteľné) — prečo je to dôležité…"
                className="w-full text-xs resize-none"
                data-testid="pin-comment-input"
                autoFocus
              />
              <button
                type="button"
                disabled={saving}
                onClick={submit}
                className="btn-primary w-full mt-2 inline-flex items-center justify-center gap-1 text-xs py-1.5"
                data-testid="pin-confirm-button"
              >
                <Check size={12} /> {saving ? "Ukladám…" : "Označiť"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
